import React, { useRef, useCallback } from 'react'
import { IconButton } from '../../../ui'

function autoResize(el) {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = el.scrollHeight + 'px'
}

const SEVERITY_STYLES = {
  low:    { bg: '#f0f0f0',   text: '#666666' },
  medium: { bg: '#e89a5d18', text: '#8a5a2a' },
  high:   { bg: '#e85d5d18', text: '#8a2a2a' },
}
const SEVERITY_CYCLE = ['low', 'medium', 'high']

export function BulletListEnriched({ items = [], onChange, maxItems = 10, maxChars = 140, placeholder = '', disabled = false }) {
  const normalized = items.length === 0 ? [{ text: '', severity: 'medium' }] : items

  function handleTextChange(index, text) {
    const next = normalized.map((item, i) => i === index ? { ...item, text } : item)
    onChange({ items: next })
  }

  function handleKeyDown(e, index) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (normalized.length < maxItems) {
        const next = [...normalized]
        next.splice(index + 1, 0, { text: '', severity: 'medium' })
        onChange({ items: next })
      }
    } else if (e.key === 'Backspace' && normalized[index].text === '' && normalized.length > 1) {
      e.preventDefault()
      onChange({ items: normalized.filter((_, i) => i !== index) })
    }
  }

  function cycleSeverity(index) {
    const current = normalized[index]?.severity ?? 'medium'
    const nextSev = SEVERITY_CYCLE[(SEVERITY_CYCLE.indexOf(current) + 1) % SEVERITY_CYCLE.length]
    const next = normalized.map((item, i) => i === index ? { ...item, severity: nextSev } : item)
    onChange({ items: next })
  }

  function handleRemove(index) {
    if (normalized.length <= 1) {
      onChange({ items: [{ text: '', severity: 'medium' }] })
    } else {
      onChange({ items: normalized.filter((_, i) => i !== index) })
    }
  }

  function handleAdd() {
    if (normalized.length < maxItems) {
      onChange({ items: [...normalized, { text: '', severity: 'medium' }] })
    }
  }

  return (
    <div style={styles.wrap}>
      {normalized.map((item, index) => {
        const sev = item.severity ?? 'medium'
        const s = SEVERITY_STYLES[sev]
        return (
          <div key={index} style={styles.row}>
            <span style={styles.prefix}>–</span>
            <textarea
              ref={(el) => autoResize(el)}
              style={styles.input}
              value={item.text}
              rows={1}
              placeholder={disabled ? '' : placeholder}
              maxLength={maxChars}
              disabled={disabled}
              onChange={(e) => { handleTextChange(index, e.target.value); autoResize(e.target) }}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-label={`Risk item ${index + 1}`}
            />
            <button
              style={{ ...styles.badge, background: s.bg, color: s.text }}
              onClick={() => !disabled && cycleSeverity(index)}
              disabled={disabled}
              aria-label={`Severity: ${sev}`}
            >
              {sev}
            </button>
            {!disabled && (
              <IconButton ariaLabel="Remove item" onClick={() => handleRemove(index)} tabIndex={-1}>×</IconButton>
            )}
          </div>
        )
      })}
      {!disabled && normalized.length < maxItems && (
        <button style={styles.addBtn} onClick={handleAdd}>+ add</button>
      )}
    </div>
  )
}

const styles = {
  wrap: { padding: '8px 14px 12px', display: 'flex', flexDirection: 'column', gap: '4px' },
  row: { display: 'flex', alignItems: 'flex-start', gap: '6px' },
  prefix: { fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.5, minWidth: '14px', flexShrink: 0, lineHeight: '1.5', paddingTop: '2px' },
  input: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: '12px', color: 'var(--text-primary)', lineHeight: '1.5', padding: '2px 0',
    resize: 'none', overflow: 'hidden', fontFamily: 'inherit',
  },
  badge: {
    fontSize: '9px', fontWeight: 600, padding: '2px 5px', borderRadius: '3px',
    border: 'none', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em',
    flexShrink: 0,
  },
  addBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px',
    color: '#3b82c4', padding: '2px 0', marginLeft: '20px', marginTop: '2px',
  },
}
